import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {NgxScannerQrcodeComponent, ScannerQRCodeConfig, ScannerQRCodeResult} from 'ngx-scanner-qrcode';
import {BehaviorSubject, lastValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {PickupService} from '../services/pickup.service';

interface CameraInfo {
  id: string;
  metaId: number;
  label: string;
  humanLabel: string;
  position?: 'front' | 'back' | 'unknown';
}

interface LocationOption {
  key: string;
  value: string;
}

@Component({
  selector: 'app-scanner-modal',
  templateUrl: './scanner-modal.component.html',
  styleUrls: ['./scanner-modal.component.scss']
})
export class ScannerModalComponent implements OnDestroy, AfterViewInit {
  @ViewChild('scanner') scanner!: NgxScannerQrcodeComponent;

  showPickerManualBultos: boolean = false;

  retryButton: boolean = false;
  retryingCameras: boolean = false;
  activeCameraId: string | null = null;
  scannerConfig: ScannerQRCodeConfig = {
    fps: 30,
    isBeep: false,
    vibrate: 0,
    constraints: {
      audio: false,
      video: {
        width: window.innerWidth,
        facingMode: {ideal: 'environment'},
        aspectRatio: 4 / 3,
      } as MediaTrackConstraints
    }
  };

  lockUserInteraction: boolean = true;

  cameras: CameraInfo[] = [];
  status = new BehaviorSubject<{
    message: string;
    type: 'success' | 'error' | 'loading';
  }>({message: 'Buscando código QR...', type: 'loading'});
  isBT: boolean = false;
  selectedLocation = {
    c1: '',
    c2: '',
    c3: '',
    c4: ''
  };
  readonly rackOptions: LocationOption[] = Array.from({length: 26}, (_, i) => ({
    key: String.fromCharCode(65 + i),
    value: String.fromCharCode(65 + i)
  }));
  readonly nivelOptions: LocationOption[] = Array.from({length: 9}, (_, i) => ({
    key: i.toString(),
    value: i.toString()
  }));
  readonly posicionOptions: LocationOption[] = Array.from({length: 21}, (_, i) => ({
    key: i.toString(),
    value: i.toString()
  }));
  readonly zonaOptions: LocationOption[] = [
    {key: 'S', value: 'Seco'},
    {key: 'F', value: 'Frío'},
    {key: 'C', value: 'Congelado'},
    {key: 'K', value: 'Carro'},
    {key: 'P', value: 'Piso'},
    {key: 'J', value: 'Jaula'},
    {key: 'X', value: 'Catex'}
  ];
  c1Options: LocationOption[] = [];
  c2Options: LocationOption[] = [];
  c3Options: LocationOption[] = [];
  c4Options: LocationOption[] = [];
  private destroy$ = new Subject<void>();
  public currentStatus: {message: string, type: 'success' | 'error' | 'loading'} = {message: 'Buscando código QR...', type: 'loading'};

  constructor(
    private dialogRef: MatDialogRef<ScannerModalComponent>,
    private pickupService: PickupService,
  ) {
    this.status.subscribe(status => {
      this.currentStatus = status;
    });
  }

  async ngAfterViewInit() {
    this.showPickerManualBultos = false;
    this.lockUserInteraction = true;
    this.retryButton = false;
    this.retryingCameras = false;

    this.initializeLocationOptions();

    this.activateCameras();
  }

  async activateCameras(){
    this.retryingCameras = true;
    await this.initializeCameras();

    if (this.scanner) this.setupScannerSubscription();
  }

  async selectCamera(device: CameraInfo) {
    try {
      if (this.scanner?.isStart) await lastValueFrom(this.scanner.stop());

      this.activeCameraId = device.id;

      if (this.scannerConfig.constraints) {
        const videoConstraints = {
          ...(this.scannerConfig.constraints.video as MediaTrackConstraints || {}),
          deviceId: {exact: device.id}
        };

        this.scannerConfig.constraints.video = videoConstraints;
      }

      this.scanner.deviceIndexActive = device.metaId;

      await lastValueFrom(this.scanner.playDevice(device.id));

      this.showStatus(`Buscando código QR...`, 'loading');

    } catch (error) {
      this.showStatus('Error al iniciar la cámara', 'error');
      console.error('Error starting camera:', error);
    }
  }

  isLocationValid(): boolean {
    return !!(this.selectedLocation.c1 &&
      this.selectedLocation.c2 &&
      this.selectedLocation.c3 &&
      this.selectedLocation.c4);
  }

  async acceptLocation(): Promise<void> {
    this.lockUserInteraction = true;
    if (this.scanner?.isStart) {
      await lastValueFrom(this.scanner.stop());
    }
    // this.dialogRef.close(this.selectedLocation);
    this.pickupService.codigoPosicion = `${this.selectedLocation.c1}${(this.pickupService.pedidoActivo.tipo == 'BT') ? '-' : ''}${this.selectedLocation.c2}-${this.selectedLocation.c3}-${this.selectedLocation.c4}`;
    this.showPickerManualBultos = true;
  }

  async cancel(): Promise<void> {
    if (this.scanner?.isStart) {
      await lastValueFrom(this.scanner.stop());
    }
    this.dialogRef.close(null);
  }

  closePickerManualBultos(){
    this.showPickerManualBultos = false;
    // this.lockUserInteraction = false;
    this.activateCameras();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.scanner?.isStart) {
      this.scanner.stop().subscribe(() => {
        console.log('Scanner stopped successfully');
      });
    }
  }

  private initializeLocationOptions(): void {
    this.isBT = this.pickupService.pedidoActivo.tipo === 'BT';

    this.c1Options = this.isBT ? [{key: 'BT', value: 'BT'}] : this.zonaOptions;
    this.c2Options = this.isBT ? [{key: '88', value: '88'}] : this.rackOptions;
    this.c3Options = this.nivelOptions;
    this.c4Options = this.posicionOptions;
  }

  private async initializeCameras() {
    try {

      this.status.next({message: 'Buscando cámaras disponibles...', type: 'loading'});

      const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      this.retryingCameras = false;

      if(this.pickupService.cachedCameras && this.pickupService.cachedCameras.length){
        if(this.pickupService.cachedCameras.every((cachedCamera) => videoDevices.find((device) => device.deviceId === cachedCamera))){
          if(this.pickupService.cachedCameras.length < videoDevices.length) {
            this.pickupService.cachedCameras = videoDevices.map((device: MediaDeviceInfo) => device.deviceId);
          }
        }else{
          return this.retryButton = true;
        }
      }else{
        return this.retryButton = true;
      }

      this.cameras = videoDevices.map((device: MediaDeviceInfo, index: number) => ({
        id: device.deviceId,
        metaId: index,
        label: device.label,
        humanLabel: this.getHumanReadableLabel(device.label),
        position: this.getCameraPosition(device.label)
      }));

      this.cameras.sort((a, b) => {
        if (a.position === 'back') return -1;
        if (b.position === 'back') return 1;
        return 0;
      });

      const backCamera: CameraInfo | undefined = this.cameras.find(cam => cam.position === 'back');

      if (backCamera) {
        this.scanner.deviceIndexActive = backCamera.metaId;
        await lastValueFrom(this.scanner.playDevice(backCamera.id));
      } else if (this.cameras.length > 0) {
        this.scanner.deviceIndexActive = this.cameras[0].metaId;
        await lastValueFrom(this.scanner.playDevice(this.cameras[0].id));
      } else {
        this.showStatus('No se encontraron cámaras', 'error');
        return;
      }

      this.showStatus('Cámaras listas', 'success');
      this.showStatus('Buscando código QR...', 'loading');
      this.lockUserInteraction = false;
    } catch (error) {
      this.lockUserInteraction = false;
      this.showStatus('No se pudo acceder a las cámaras', 'error');
      console.error('Error accessing cameras:', error);
    }
  }

  private setupScannerSubscription(): void {
    this.scanner.data.pipe(
      takeUntil(this.destroy$)
    ).subscribe(results => {
      if (results?.length > 0) {
        this.handleScan(results);
      }
    });
  }

  private handleScan(results: ScannerQRCodeResult[]) {
    if (!results?.[0]?.value) return;

    const scannedValue = results[0].value;

    if (!scannedValue.includes('-')) {
      this.showStatus('El código debe contener guiones (ejemplo: A-B-11-2)', 'error');
      return;
    }

    if (!this.isValidQRFormat(scannedValue)) {
      this.showStatus('Formato inválido: Debe tener 4 caracteres separados por guiones', 'error');
      return;
    }

    const values = scannedValue.split('-');
    this.selectedLocation = {
      c1: values[0],
      c2: values[1],
      c3: values[2],
      c4: values[3]
    };

    this.showStatus('✅ Ubicación escaneada correctamente', 'success');
    if (navigator.vibrate) navigator.vibrate(200);

    setTimeout(() => {
      this.acceptLocation();
    }, 1000);
  }

  private getHumanReadableLabel(label: string): string {
    const labelLower = label.toLowerCase();
    if (labelLower.includes('back') || labelLower.includes('trasera')) {
      return '📸 Cámara Trasera';
    } else if (labelLower.includes('front') || labelLower.includes('frontal')) {
      return '🤳 Cámara Frontal';
    }
    return '📱 Otra Cámara';
  }

  private getCameraPosition(label: string): 'front' | 'back' | 'unknown' {
    const labelLower = label.toLowerCase();
    if (labelLower.includes('back') || labelLower.includes('trasera')) {
      return 'back';
    } else if (labelLower.includes('front') || labelLower.includes('frontal')) {
      return 'front';
    }
    return 'unknown';
  }

  private isValidQRFormat(code: string): boolean {
    return /^[a-zA-Z0-9]{1,2}-[a-zA-Z0-9]{1,2}-[a-zA-Z0-9]{1,2}-[a-zA-Z0-9]{1,2}$/.test(code);
  }

  private showStatus(message: string, type: 'success' | 'error' | 'loading'): void {
    this.status.next({message, type});

    if (type === 'success') {
      setTimeout(() => {
        if (this.status.value.message === message) {
          this.showStatus('Buscando código QR...', 'loading');
        }
      }, 2000);
    }
  }
}